import { FC, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useAuth } from '../../modules/auth';
import { ConfirmDialog } from '../../components/design-system';
import { SensorTransportProvider } from './transports/SensorTransportProvider';
import { useAlarmedBoxes } from './hooks/useAlarmedBoxes';
import { SensorFilters } from './types/sensors';
import { SensorsToolbar } from './components/SensorsToolbar';
import { BoxesGrid } from './components/BoxesGrid';
import { EmptyState } from './components/EmptyState';
import { BoxTimelineDrawer } from './components/BoxTimelineDrawer';
import { clearBoxAlarmsBatch } from './core/_requests';

const initialFilters: SensorFilters = {
  sort: 'gravity_desc', page: 1, items_per_page: 20,
};

type ConfirmState =
  | { kind: 'one'; box_id: number; cmb_number: string }
  | { kind: 'all'; count: number }
  | null;

const SensorsV2Inner: FC = () => {
  const { currentUser } = useAuth();
  const [filters, setFilters] = useState<SensorFilters>({
    ...initialFilters,
    company_id: currentUser?.last_company_id ? +currentUser.last_company_id : undefined,
  });
  const [selectedBoxId, setSelectedBoxId] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [clearing, setClearing] = useState(false);

  const { data, isLoading } = useAlarmedBoxes(filters);
  const queryClient = useQueryClient();

  const boxes = useMemo(() => {
    const all = data?.data ?? [];
    if (!filters.firmware_protocol) return all;
    return all.filter((b) => b.firmware_protocol === filters.firmware_protocol);
  }, [data?.data, filters.firmware_protocol]);
  const totalsByType = data?.payload?.totals_by_type ?? {};
  const selectedBox = useMemo(
    () => boxes.find((b) => b.box_id === selectedBoxId) ?? null,
    [boxes, selectedBoxId],
  );

  const hasActiveFilters = !!filters.cmb_number || !!filters.alarm_types?.length || !!filters.project_id || !!filters.firmware_protocol;

  const requestClearOne = (box_id: number) => {
    const box = boxes.find((b) => b.box_id === box_id);
    if (!box) return;
    setConfirm({ kind: 'one', box_id, cmb_number: String(box.cmb_number ?? '') });
  };

  const requestClearAll = () => {
    if (!boxes.length) return;
    setConfirm({ kind: 'all', count: boxes.length });
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setClearing(true);
    try {
      if (confirm.kind === 'one') {
        const box = boxes.find((b) => b.box_id === confirm.box_id);
        if (!box) return;
        await clearBoxAlarmsBatch(confirm.box_id, box.active_alarms.map((a) => a.type));
        toast.success(`Alarmes limpos para CMB ${box.cmb_number}`);
      } else {
        for (const b of boxes) {
          await clearBoxAlarmsBatch(b.box_id, b.active_alarms.map((a) => a.type));
        }
        toast.success('Alarmes limpos.');
      }
      // Bring every alarm-aware surface in sync — sensors-v2 list, the
      // header bell badge, the dashboard heatmap. The WS gateway doesn't
      // emit alarm_changed for UI-initiated clears (only firmware-driven
      // state changes), so we have to fan out manually.
      queryClient.invalidateQueries(['sensors-v2', 'alarmed-boxes']);
      queryClient.invalidateQueries(['alarm-bell']);
      queryClient.invalidateQueries(['dashboard-stats']);
      setConfirm(null);
    } catch (e: any) {
      toast.error(`Erro ao limpar: ${e?.message ?? 'desconhecido'}`);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div style={{ paddingRight: selectedBox ? 380 : 0, transition: 'padding-right 0.2s' }}>
      <SensorsToolbar
        filters={filters}
        totalsByType={totalsByType}
        onChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
        canClearAll={boxes.length > 0}
        onClearAll={requestClearAll}
      />
      {isLoading && <div className="text-muted">Carregando...</div>}
      {!isLoading && boxes.length === 0 && !hasActiveFilters && <EmptyState variant="no-alarms" />}
      {!isLoading && boxes.length === 0 && hasActiveFilters && (
        <EmptyState variant="no-matches" onClearFilters={() => setFilters(initialFilters)} />
      )}
      {boxes.length > 0 && (
        <BoxesGrid
          boxes={boxes}
          selectedBoxId={selectedBoxId}
          onSelect={(id) => setSelectedBoxId(id)}
          onClearAlarms={requestClearOne}
        />
      )}
      {selectedBox && (
        <BoxTimelineDrawer
          box_id={selectedBox.box_id}
          cmb_number={selectedBox.cmb_number}
          company_name={selectedBox.company_name}
          project_name={selectedBox.project_name}
          onClose={() => setSelectedBoxId(null)}
        />
      )}

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === 'all' ? 'Limpar todos os alarmes?' : 'Limpar alarmes da caixa?'}
        description={
          confirm?.kind === 'all' ? (
            <>
              Vai limpar os alarmes ativos de <strong>{confirm.count}</strong> caixa{confirm.count > 1 ? 's' : ''}.
              {' '}A ação não pode ser desfeita.
            </>
          ) : confirm?.kind === 'one' ? (
            <>
              Vai limpar todos os alarmes ativos da caixa <strong>CMB {confirm.cmb_number}</strong>.
              {' '}A ação não pode ser desfeita.
            </>
          ) : null
        }
        confirmLabel={confirm?.kind === 'all' ? 'Limpar todos' : 'Limpar'}
        tone='danger'
        loading={clearing}
        onConfirm={handleConfirm}
        onCancel={() => { if (!clearing) setConfirm(null); }}
      />
    </div>
  );
};

const SensorsV2Page: FC = () => (
  <SensorTransportProvider transport="websocket">
    <SensorsV2Inner />
  </SensorTransportProvider>
);

export { SensorsV2Page };
